import { useMemo, useState } from 'react';

import { EntityLinkPicker } from './EntityLinkPicker';
import {
  buildDeepLink,
  findTarget,
  DEEP_LINK_CUSTOM,
  DEEP_LINK_NONE,
  DEEP_LINK_TARGETS,
  LEGACY_DEEP_LINK_TARGETS,
  type ParsedDeepLink,
} from '../Banners/deepLinkTargets';
import {
  PICKABLE_ENTITY_LABELS,
  type PickableEntityType,
} from '../../services/pickableEntities';

/**
 * The deep-link half of a composer, shared by the push notification composer
 * and the banner editor.
 *
 * The two had the same twenty lines of JSX side by side — a `<select>`, a
 * free-text id box, a custom-URL box, an error line and a preview. Extracting
 * it is what makes the picker work land on both surfaces at once instead of
 * push getting typeahead and banners keeping a box you paste ids into.
 *
 * The id field is now a pick for every entity route we offer. It is still a
 * TEXT field for the two legacy per-user routes, which no longer appear in the
 * dropdown but can still arrive from a stored record — see
 * `LEGACY_DEEP_LINK_TARGETS`.
 */
export interface DeepLinkFieldProps {
  value: ParsedDeepLink;
  onChange: (next: ParsedDeepLink) => void;
  /** Tailwind classes for the select/input, so each composer keeps its look. */
  inputClass: string;
  labelClass: string;
  label?: string;
}

export function DeepLinkField({
  value,
  onChange,
  inputClass,
  labelClass,
  label = 'Deep link',
}: DeepLinkFieldProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const built = useMemo(() => buildDeepLink(value), [value]);
  const error = built.error;

  const activeTarget =
    value.mode !== DEEP_LINK_NONE && value.mode !== DEEP_LINK_CUSTOM
      ? findTarget(value.mode)
      : undefined;

  /**
   * A stored record can name a route the dropdown no longer lists. Rather than
   * silently showing the first option — which would rewrite the link on the
   * next save — the retired target is appended for as long as it is selected.
   */
  const retiredTarget = LEGACY_DEEP_LINK_TARGETS.find(
    (t) => t.key === value.mode,
  );

  const pickType = activeTarget?.pick;

  const set = (patch: Partial<ParsedDeepLink>) => onChange({ ...value, ...patch });

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select
        value={value.mode}
        onChange={(e) =>
          // Clear the id and its label: they belong to the type being left, and
          // carrying a brand id into the module field produces a link that is
          // well-formed and wrong.
          onChange({
            mode: e.target.value,
            id: '',
            customUrl: value.customUrl,
            label: undefined,
          })
        }
        className={inputClass}
      >
        <option value={DEEP_LINK_NONE}>None</option>
        {DEEP_LINK_TARGETS.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
          </option>
        ))}
        {retiredTarget && (
          <option value={retiredTarget.key}>{retiredTarget.label}</option>
        )}
        <option value={DEEP_LINK_CUSTOM}>Custom URL…</option>
      </select>

      {activeTarget?.help && (
        <p className="text-xs text-gray-500 mt-1">{activeTarget.help}</p>
      )}

      {/* Entity routes: pick by name. */}
      {pickType && (
        <div className="mt-2 flex items-center gap-2">
          {value.id ? (
            <span className="inline-flex items-center gap-2 min-w-0 rounded-full bg-gray-100 border border-gray-300 px-3 py-1 text-sm">
              <span className="font-medium text-gray-900 truncate">
                {value.label || value.id}
              </span>
              {/* A re-parsed record has no stored label, so the name slot is
                  already showing the id — repeating it beside itself reads as
                  a rendering bug rather than as extra information. */}
              {value.label && value.label !== value.id && (
                <span className="text-xs text-gray-500 font-mono truncate">
                  {value.id}
                </span>
              )}
            </span>
          ) : (
            <span className="text-sm text-gray-500">
              No {PICKABLE_ENTITY_LABELS[pickType].toLowerCase()} chosen
            </span>
          )}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="text-sm text-brand-600 hover:text-brand-700 underline shrink-0"
          >
            {value.id ? 'Change' : 'Choose…'}
          </button>
          {value.id && (
            <button
              type="button"
              onClick={() => set({ id: '', label: undefined })}
              className="text-sm text-gray-500 hover:text-gray-700 shrink-0"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Legacy per-user routes keep the raw id box they always had. */}
      {activeTarget?.idParam && !pickType && (
        <input
          type="text"
          value={value.id}
          onChange={(e) => set({ id: e.target.value })}
          placeholder={activeTarget.idParam}
          className={`${inputClass} mt-2 ${error ? 'border-red-400' : ''}`}
        />
      )}

      {/* A static route's optional number param — What's New's days window. */}
      {activeTarget?.optionalNumberParam && (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {activeTarget.optionalNumberParam.label}
          </label>
          <input
            type="number"
            inputMode="numeric"
            min={activeTarget.optionalNumberParam.min}
            max={activeTarget.optionalNumberParam.max}
            step={1}
            value={value.param ?? ''}
            onChange={(e) => set({ param: e.target.value })}
            placeholder={activeTarget.optionalNumberParam.placeholder}
            aria-label={activeTarget.optionalNumberParam.label}
            className={`${inputClass} ${error ? 'border-red-400' : ''}`}
          />
        </div>
      )}

      {value.mode === DEEP_LINK_CUSTOM && (
        <input
          type="text"
          value={value.customUrl}
          onChange={(e) => set({ customUrl: e.target.value })}
          placeholder="https://example.com/promo"
          className={`${inputClass} mt-2 ${error ? 'border-red-400' : ''}`}
        />
      )}

      {activeTarget?.requiresAppHint && (
        <p className="text-xs text-amber-700 mt-1">
          {activeTarget.requiresAppHint}
        </p>
      )}

      {error ? (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      ) : (
        built.value && (
          <p className="text-xs text-gray-400 mt-1 font-mono break-all">
            → {built.value}
          </p>
        )
      )}

      {pickType && (
        <EntityLinkPicker<PickableEntityType>
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          // One tab: the dropdown already chose the entity type, and letting
          // the modal change it would leave the two disagreeing.
          types={[pickType]}
          initialType={pickType}
          title={`Choose a ${PICKABLE_ENTITY_LABELS[pickType].toLowerCase()}`}
          notice={activeTarget?.requiresAppHint}
          onSelect={(selection) => {
            set({ id: selection.id, label: selection.label });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
